/**
 * A/B Testing API — Mock 版（对标巨量引擎A/B测试）
 *
 * 4 种状态:
 *   draft         — 草稿
 *   running       — 运行中
 *   ended         — 已结束
 *   winner_declared — 已获胜
 *
 * 3 种测试类型:
 *   creative  — 素材测试
 *   title     — 标题测试
 *   landing   — 落地页测试
 */

// 模拟网络延迟
const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));

// 生成变体工具
const makeVariants = (testType, count) => {
  const prefixes = {
    creative: ['视频版A', '视频版B', '图片版C', '动图版D', '轮播版E'],
    title: ['标题方案A', '标题方案B', '标题方案C', '标题方案D'],
    landing: ['落地页V1', '落地页V2', '落地页V3', '落地页V4'],
  };
  const names = prefixes[testType] || prefixes.creative;
  const baseImpressions = 8000 + Math.floor(Math.random() * 40000);
  const trafficSplit = Math.floor(100 / count);
  const remainder = 100 - trafficSplit * count;

  return Array.from({ length: count }, (_, i) => {
    const impressions = baseImpressions + Math.floor(Math.random() * 12000);
    const clicks = Math.floor(impressions * (0.02 + Math.random() * 0.08));
    const conversions = Math.floor(clicks * (0.05 + Math.random() * 0.2));
    const rate = conversions / clicks;
    return {
      id: `variant_${i + 1}`,
      name: names[i] || `变体${i + 1}`,
      trafficRatio: i === 0 ? trafficSplit + remainder : trafficSplit,
      impressions,
      clicks,
      conversions,
      conversionRate: +(rate * 100).toFixed(2),
    };
  });
};

// 8 个 Mock 测试实验
let mockTests = [
  {
    id: 1,
    name: 'Q4黑五促销素材测试',
    type: 'creative',
    status: 'running',
    startTime: '2026-08-01 09:00',
    endTime: null,
    winnerVariant: null,
    confidence: 87.3,
    trafficSplitMode: 'equal',
    description: '黑五促销期间3支不同创意视频，测试哪版CTR最高',
    variants: makeVariants('creative', 3),
  },
  {
    id: 2,
    name: '夏季新品标题A/B实验',
    type: 'title',
    status: 'winner_declared',
    startTime: '2026-07-15 10:00',
    endTime: '2026-07-29 18:00',
    winnerVariant: '标题方案B',
    confidence: 96.8,
    trafficSplitMode: 'equal',
    description: '测试不同标题文案对点击率的提升效果',
    variants: makeVariants('title', 2),
  },
  {
    id: 3,
    name: '电商落地页转化优化',
    type: 'landing',
    status: 'running',
    startTime: '2026-08-05 14:00',
    endTime: null,
    winnerVariant: null,
    confidence: 62.1,
    trafficSplitMode: 'manual',
    description: '新版落地页VS旧版，测试表单提交转化率差异',
    variants: makeVariants('landing', 2),
  },
  {
    id: 4,
    name: '品牌曝光视频策略实验',
    type: 'creative',
    status: 'ended',
    startTime: '2026-06-20 08:00',
    endTime: '2026-07-20 23:59',
    winnerVariant: null,
    confidence: 45.2,
    trafficSplitMode: 'equal',
    description: '品牌曝光方向创意素材测试，差异不够显著',
    variants: makeVariants('creative', 4),
  },
  {
    id: 5,
    name: '双十一预热标题测试',
    type: 'title',
    status: 'draft',
    startTime: null,
    endTime: null,
    winnerVariant: null,
    confidence: 0,
    trafficSplitMode: 'equal',
    description: '双十一活动预热期，探索爆款标题文案方向',
    variants: [],
  },
  {
    id: 6,
    name: '独立站落地页改版实验',
    type: 'landing',
    status: 'running',
    startTime: '2026-08-08 10:00',
    endTime: null,
    winnerVariant: null,
    confidence: 95.7,
    trafficSplitMode: 'equal',
    description: '新版简约设计落地页测试，已接近显著水平',
    variants: makeVariants('landing', 2),
  },
  {
    id: 7,
    name: 'TikTok挑战赛素材实验',
    type: 'creative',
    status: 'winner_declared',
    startTime: '2026-07-01 12:00',
    endTime: '2026-07-15 12:00',
    winnerVariant: '视频版A',
    confidence: 98.2,
    trafficSplitMode: 'equal',
    description: 'TikTok挑战赛不同开场形式效果测试',
    variants: makeVariants('creative', 3),
  },
  {
    id: 8,
    name: 'Q3邮件营销主题行测试',
    type: 'title',
    status: 'ended',
    startTime: '2026-06-01 09:00',
    endTime: '2026-06-22 09:00',
    winnerVariant: '标题方案A',
    confidence: 91.5,
    trafficSplitMode: 'equal',
    description: 'EDM主题行文案测试，寻找最高打开率方案',
    variants: makeVariants('title', 4),
  },
];

let nextId = 9;

/**
 * 获取测试活动列表
 */
export const getTests = async () => {
  await delay();
  return [...mockTests];
};

/**
 * 获取单个测试详情
 * @param {number} id 测试ID
 */
export const getTestDetail = async (id) => {
  await delay(200);
  const test = mockTests.find((t) => t.id === id);
  if (!test) return Promise.reject(new Error('测试不存在'));
  return { ...test, variants: [...test.variants] };
};

/**
 * 创建新测试实验
 * @param {object} data 实验配置
 */
export const createTest = async (data) => {
  await delay(400);
  const count = data.variantCount || 3;
  const newTest = {
    id: nextId++,
    name: data.name,
    type: data.testType || 'creative',
    status: 'draft',
    startTime: null,
    endTime: null,
    winnerVariant: null,
    confidence: 0,
    trafficSplitMode: data.trafficSplitMode || 'equal',
    description: data.description || '',
    variants: [],
  };
  // 如果立即启动
  if (data.startImmediately) {
    newTest.status = 'running';
    newTest.startTime = new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-').slice(0, 16);
    newTest.variants = makeVariants(newTest.type, count);
  }
  mockTests.unshift(newTest);
  return { ...newTest };
};

/**
 * 声明胜出变体
 * @param {number} id 测试ID
 * @param {string} variantName 胜出变体名称
 */
export const declareWinner = async (id, variantName) => {
  await delay(300);
  const test = mockTests.find((t) => t.id === id);
  if (!test) return Promise.reject(new Error('测试不存在'));
  test.status = 'winner_declared';
  test.winnerVariant = variantName;
  test.endTime = new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-').slice(0, 16);
  return { success: true, id, winnerVariant: variantName };
};

/**
 * 结束实验
 * @param {number} id 测试ID
 */
export const endTest = async (id) => {
  await delay(300);
  const test = mockTests.find((t) => t.id === id);
  if (!test) return Promise.reject(new Error('测试不存在'));
  test.status = 'ended';
  test.endTime = new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-').slice(0, 16);
  return { success: true, id };
};

/**
 * 启动实验（从草稿状态）
 * @param {number} id 测试ID
 */
export const startTest = async (id) => {
  await delay(300);
  const test = mockTests.find((t) => t.id === id);
  if (!test) return Promise.reject(new Error('测试不存在'));
  if (test.status !== 'draft') return Promise.reject(new Error('仅草稿状态可启动'));
  test.status = 'running';
  test.startTime = new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-').slice(0, 16);
  const count = test.variants.length || 3;
  test.variants = makeVariants(test.type, count);
  return { ...test };
};

export default {
  getTests,
  getTestDetail,
  createTest,
  declareWinner,
  endTest,
  startTest,
};
